import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Bell } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { AsoloopLogo } from "@/components/brand/AsoloopLogo";
import MemorySearchBar from "@/components/header/MemorySearchBar";
import LocaleSelect from "@/components/layout/LocaleSelect";
import TreeSelect, { type AppTreeItem } from "@/components/ui/TreeSelect";
import UserMenu from "@/components/auth/UserMenu";
import { cn } from "@/lib/utils";
import { useApps, useAppDetail, useSelectApp } from "@/hooks/useApps";
import { useSelectedAppContext } from "@/lib/useSelectedAppContext";
import {
  effectiveMode,
  systemModeChipClass,
  systemModeLabel,
} from "@/lib/systemMode";
import Tooltip from "@/components/ui/Tooltip";
import BellPanel from "@/components/layout/BellPanel";
import { useNeedsYou, useNeedsYouRefetchOnMutation } from "@/hooks/useNeedsYou";

/**
 * Global header chrome.
 *
 * Source: BRIEF_ia-redesign-frontend-implementation § 5.1.
 * Layout: [Logo] [TreeSelect Apps tree] [LocaleSelect] ... [Mode badge] [Bell]
 * [Avatar ▾]
 *
 * The locale selector sits BESIDE the app TreeSelect so app-scope and
 * locale-scope read as one control pair (ASOLOOP-UX item 1). It is the app's
 * ONLY locale switcher — the page-level control Creatives used to own was
 * retired into it. `null` means ALL locales (an explicit scope, not "unset"),
 * and the scope is deliberately NOT persisted and NOT URL-derived: it resets
 * on app switch via a render-time owner check rather than a `useEffect`
 * setState, so no frame ever renders the previous app's filter. Pinned by
 * `tests/verify/verify_sidebar_accordion_runtime.py::verify_global_locale_selector`.
 *
 * R1b unblock (2026-05-09): the Mode badge reads `App.system_mode`
 * (Direction #34 three-mode lifecycle — `learning | classic | agent`)
 * via `useAppDetail`, resolved through `effectiveMode` so an operator
 * override wins. While the detail query is in-flight or before an app is
 * selected, the badge is hidden. The Apps Tree rows read the same
 * lifecycle mode off `AppSummaryOut` (which carries `system_mode` +
 * `operator_mode_override`), so every mode surface agrees on the app.
 */

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    workspaceId,
    userId,
    userDisplayName,
    selectedAppId,
    setSelectedAppId,
  } = useSelectedAppContext();
  const { data: appsData } = useApps(workspaceId, userId);
  const { data: appDetail } = useAppDetail(selectedAppId);
  const selectAppMutation = useSelectApp();

  const apps: AppTreeItem[] = (appsData?.apps ?? []).map((app) => ({
    id: app.app_id,
    // Render the resolved brand label, never the numeric store_id fallback
    // (ENG-APP-CONFIG-IA-OVERHAUL Track A; PO 2026-06-16).
    name: app.brand_label,
    platform: app.platform,
    mode: effectiveMode(app.system_mode, app.operator_mode_override),
    runningCount: app.running_experiment_count,
    consoleCount: app.running_console_count,
    iconUrl: app.icon_url,
  }));

  const badgeMode = appDetail
    ? effectiveMode(appDetail.system_mode, appDetail.operator_mode_override)
    : null;
  const overrideActive =
    appDetail !== undefined &&
    appDetail.operator_mode_override !== "follow_system";

  function handleSelectApp(appId: string) {
    if (userId === null) return;
    setSelectedAppId(appId);
    selectAppMutation.mutate({ appId, userId });
  }

  function handleAddNew() {
    navigate("/onboarding");
  }

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between gap-4 border-b border-divider bg-surface px-4",
      )}
    >
      <div className="flex items-center gap-3">
        <NavLink
          to="/"
          aria-label="ASOLOOP home"
          className="flex items-center text-ink"
        >
          <AsoloopLogo className="text-xl" />
        </NavLink>
        <div className="ml-2">
          <TreeSelect
            apps={apps}
            selected={selectedAppId}
            onSelect={handleSelectApp}
            onAddNew={handleAddNew}
            onTeamSpace={() => navigate("/team")}
            disabled={apps.length === 0}
          />
        </div>
        {/* Global locale selector (ASOLOOP-UX item 1) — sits beside the Apps
            tree because both are global scope selectors. Placed BEFORE
            MemorySearchBar so the TreeSelect < MemorySearchBar < Bell ordering
            pinned by verify_memory_search_bar_ui.py is preserved. */}
        <LocaleSelect />
      </div>
      <div className="mx-4 hidden flex-1 justify-center md:flex">
        <MemorySearchBar appId={selectedAppId} />
      </div>
      <div className="flex items-center gap-3">
        {badgeMode && (
          <Tooltip
            content={t(
              overrideActive
                ? "layout.header.modeTitleOverride"
                : "layout.header.modeTitleAuto",
            )}
          >
            <button
              type="button"
              onClick={() => navigate("/app/ai-settings")}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                systemModeChipClass[badgeMode],
                "hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
              )}
              aria-label={t(
                overrideActive
                  ? "layout.header.modeAriaLabelOverride"
                  : "layout.header.modeAriaLabel",
                { mode: systemModeLabel[badgeMode] },
              )}
            >
              {t("layout.header.modeChip", {
                mode: systemModeLabel[badgeMode],
              })}
              {overrideActive && (
                <span className="ml-1 opacity-75" aria-hidden="true">
                  •
                </span>
              )}
            </button>
          </Tooltip>
        )}
        <NotificationsBell appId={selectedAppId} />
        <UserMenu displayName={userDisplayName} />
      </div>
    </header>
  );
}

/**
 * The bell (in-app design spec § 12 § 3.1: "bell (count = Needs you; name
 * 'Notifications: 3 need you')"). Its count is the ONE Needs-you list's
 * length — the same query the Dashboard and Active render — and it opens a
 * non-modal panel (focus moves in, Esc or a click outside closes it and
 * focus returns to the bell) with the *Needs you* and *Updates* tabs
 * (`BellPanel`). It replaces the interim link to the notification settings,
 * which stay one click away from the panel's footer.
 */
function NotificationsBell({ appId }: { appId: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const needsYou = useNeedsYou(appId);
  // ONE subscription for the whole app (the bell is always mounted).
  useNeedsYouRefetchOnMutation(appId);
  const count = needsYou.data?.count ?? 0;
  const failed = needsYou.isError && !needsYou.data;
  // Controlled elements (the pattern @floating-ui/react documents for React
  // 19 + the React Compiler lint), as in Tooltip.tsx.
  const [referenceEl, setReferenceEl] = useState<HTMLElement | null>(null);
  const [floatingEl, setFloatingEl] = useState<HTMLElement | null>(null);
  const { floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-end",
    elements: { reference: referenceEl, floating: floatingEl },
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return (
    <>
      <button
        ref={setReferenceEl}
        type="button"
        className={cn(
          "relative rounded-md p-2 text-muted transition-colors",
          "hover:bg-divider-soft hover:text-ink-soft",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        )}
        aria-label={
          failed
            ? t("layout.header.notificationsAriaLabelError")
            : count > 0
              ? t("layout.header.notificationsAriaLabelCount", { count })
              : t("layout.header.notificationsAriaLabel")
        }
        {...getReferenceProps()}
      >
        <Bell size={18} aria-hidden="true" />
        {(count > 0 || failed) && (
          <span
            aria-hidden="true"
            data-testid="bell-count"
            className="absolute -right-1 -top-1 min-w-5 rounded-full bg-error-text px-1 text-center text-xs font-bold leading-5 text-surface"
          >
            {failed ? "!" : count}
          </span>
        )}
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={setFloatingEl}
              style={floatingStyles}
              aria-label={t("layout.bell.dialogLabel")}
              {...getFloatingProps()}
              className="z-50 rounded-xl border border-divider bg-surface shadow-lg"
            >
              <BellPanel appId={appId} onClose={() => setOpen(false)} />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
